import { useEffect, useMemo, useState } from 'react';
import type { Train } from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import { useGetTrainQuery, useListTrainsQuery } from '@frontend/api-sync-config';
import { useDebouncedValue } from '@frontend/design-system';
import { getTotalTrains, normalizeTrain } from '../helpers';

const TRAINS_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 500;

interface UseReplacementTrainsOptions {
  enabled: boolean;
  onLoadMoreError?: (error: Error) => void;
}

export const useReplacementTrains = ({ enabled, onLoadMoreError }: UseReplacementTrainsOptions) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedTrainId = useDebouncedValue(searchQuery.trim(), SEARCH_DEBOUNCE_MS);
  const isSearching = searchQuery.trim() !== '';
  const isSearchPending = searchQuery.trim() !== debouncedTrainId;

  const {
    data: trainsData,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingTrains,
    isFetchingNextPage: isLoadingMoreTrains,
    isError: isTrainsError,
  } = useListTrainsQuery({
    request: { pageSize: TRAINS_PAGE_SIZE },
    options: { enabled },
    httpOptions: { skipValidation: true },
  });

  const {
    data: searchedTrainData,
    isFetching: isFetchingSearchedTrain,
    isError: isSearchError,
  } = useGetTrainQuery({
    request: { trainId: debouncedTrainId },
    options: {
      enabled: enabled && debouncedTrainId !== '',
      retry: false,
    },
    httpOptions: { skipValidation: true },
  });

  useEffect(() => {
    if (!enabled) setSearchQuery('');
  }, [enabled]);

  const trains = useMemo<Train[]>(() => {
    const trainsById = new Map<string, Train>();

    for (const page of trainsData?.pages ?? []) {
      for (const train of page.trains ?? []) {
        const normalizedTrain = normalizeTrain(train);
        if (normalizedTrain?.trainId) trainsById.set(normalizedTrain.trainId, normalizedTrain);
      }
    }

    return Array.from(trainsById.values());
  }, [trainsData]);

  const searchedTrain = useMemo(
    () => (searchedTrainData ? normalizeTrain(searchedTrainData) : undefined),
    [searchedTrainData]
  );

  const loadMoreTrains = async () => {
    const result = await fetchNextPage();
    if (result.isError) onLoadMoreError?.(result.error);
  };

  return {
    trains,
    totalTrains: getTotalTrains(trainsData?.pages.at(-1)),
    hasMoreTrains: Boolean(hasNextPage),
    isLoadingTrains,
    isLoadingMoreTrains,
    isTrainsError,
    loadMoreTrains,
    searchQuery,
    setSearchQuery,
    searchedTrain,
    isSearching,
    isSearchPending: isSearchPending || isFetchingSearchedTrain,
    isSearchError,
  };
};
